import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { fileUrl } from '../utils/api';

const V1 = '#7F00FF';
const V4 = '#2B0057';
const isExternal = link => /^https?:\/\//i.test(link || '');

const AnnouncementBanner = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    API.get('/announcements/active').then(r => setAnnouncement(r.data)).catch(() => {});
  }, []);

  if (!announcement || !announcement._id) return null;

  const { title, message, imageUrl, buttons } = announcement;

  return (
    <div
      className="announcement-banner"
      style={{
        background: `linear-gradient(135deg, ${V4} 0%, #50108f 45%, ${V1} 100%)`,
      }}
    >
      <div className="announcement-banner-grid">
        {imageUrl && (
          <button
            type="button"
            className="announcement-banner-media"
            onClick={() => setLightboxOpen(true)}
            aria-label="Open announcement poster"
          >
            <img
              src={fileUrl(imageUrl)}
              alt={title || 'Announcement poster'}
              className="announcement-banner-img"
            />
          </button>
        )}

        <div className="announcement-banner-copy">
          <div className="announcement-banner-kicker">Live announcement</div>

          {title && <h3 className="announcement-banner-title">{title}</h3>}

          <p className="announcement-banner-message">{message}</p>

          {buttons && buttons.length > 0 && (
            <div className="announcement-banner-actions">
              {buttons.map((b, i) => {
                const pill = (
                  <span className={i === 0 ? 'announcement-banner-btn primary' : 'announcement-banner-btn secondary'}>
                    {b.text}
                  </span>
                );
                const commonProps = {
                  key: i,
                  className: 'announcement-banner-link',
                  onMouseEnter: e => e.currentTarget.style.transform = 'translateY(-2px)',
                  onMouseLeave: e => e.currentTarget.style.transform = 'translateY(0)',
                };

                return isExternal(b.link)
                  ? <a {...commonProps} href={b.link} target="_blank" rel="noreferrer">{pill}</a>
                  : <Link {...commonProps} to={b.link}>{pill}</Link>;
              })}
            </div>
          )}
        </div>
      </div>

      {lightboxOpen && (
        <div className="announcement-banner-lightbox" onClick={() => setLightboxOpen(false)}>
          <div className="announcement-banner-lightbox-panel" onClick={e => e.stopPropagation()}>
            <img src={fileUrl(imageUrl)} alt={title || 'Announcement poster'} />
            <button
              className="announcement-banner-lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="Close poster preview"
            />
          </div>
        </div>
      )}

      <style>{`
        .announcement-banner {
          position: relative;
          width: 100%;
          min-height: 300px;
          margin-bottom: 52px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 28px;
          color: #fff;
          box-shadow: 0 24px 80px rgba(46,0,87,0.38);
        }
        .announcement-banner::before,
        .announcement-banner::after {
          content: "";
          position: absolute;
          pointer-events: none;
          border-radius: 999px;
        }
        .announcement-banner::before {
          inset: -90px auto auto -70px;
          width: 260px;
          height: 260px;
          background: rgba(255,255,255,0.13);
          filter: blur(16px);
        }
        .announcement-banner::after {
          right: -90px;
          bottom: -110px;
          width: 360px;
          height: 360px;
          background: rgba(255,209,102,0.24);
          filter: blur(34px);
        }
        .announcement-banner-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: minmax(190px, 300px) minmax(0, 1fr);
          align-items: center;
          gap: clamp(28px, 5vw, 72px);
          min-height: 300px;
          padding: clamp(28px, 5vw, 58px);
        }
        .announcement-banner-media {
          width: 100%;
          aspect-ratio: 4 / 5;
          max-height: 250px;
          border: 0;
          border-radius: 18px;
          padding: 0;
          background: rgba(12,0,24,0.32);
          box-shadow: 0 20px 55px rgba(0,0,0,0.36);
          cursor: zoom-in;
          overflow: hidden;
        }
        .announcement-banner-img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
        }
        .announcement-banner-copy {
          max-width: 680px;
          text-align: left;
        }
        .announcement-banner-kicker {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          margin-bottom: 14px;
          padding: 6px 13px;
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 999px;
          background: rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.92);
          font-size: 11px;
          font-weight: 900;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .announcement-banner-title {
          max-width: 760px;
          margin: 0 0 12px;
          color: #fff;
          font-family: 'Inter',sans-serif;
          font-size: clamp(32px, 4.6vw, 58px);
          font-weight: 900;
          line-height: 0.98;
          letter-spacing: 0;
        }
        .announcement-banner-message {
          max-width: 600px;
          margin: 0;
          color: rgba(255,255,255,0.86);
          font-size: clamp(15.5px, 1.7vw, 19px);
          font-weight: 700;
          line-height: 1.55;
        }
        .announcement-banner-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 26px;
        }
        .announcement-banner-link {
          display: inline-block;
          text-decoration: none;
          transition: transform 0.16s ease;
        }
        .announcement-banner-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 150px;
          min-height: 48px;
          padding: 13px 24px;
          border-radius: 12px;
          font-size: 14.5px;
          font-weight: 900;
          white-space: nowrap;
        }
        .announcement-banner-btn.primary {
          background: #fff;
          color: ${V1};
          box-shadow: 0 14px 34px rgba(0,0,0,0.24);
        }
        .announcement-banner-btn.secondary {
          border: 1px solid rgba(255,255,255,0.34);
          background: rgba(255,255,255,0.12);
          color: #fff;
        }
        .announcement-banner-lightbox {
          position: fixed;
          inset: 0;
          z-index: 7000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(0,0,0,0.92);
        }
        .announcement-banner-lightbox-panel {
          position: relative;
          display: inline-flex;
          max-width: min(86vw, 720px);
          max-height: 78vh;
        }
        .announcement-banner-lightbox-panel img {
          max-width: min(86vw, 720px);
          max-height: 78vh;
          border-radius: 10px;
          object-fit: contain;
        }
        .announcement-banner-lightbox-close {
          position: absolute;
          top: -14px;
          right: -14px;
          width: 34px;
          height: 34px;
          border: 0;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 10px 28px rgba(0,0,0,0.36);
          cursor: pointer;
        }
        .announcement-banner-lightbox-close::before,
        .announcement-banner-lightbox-close::after {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 15px;
          height: 2px;
          border-radius: 999px;
          background: #2B0057;
        }
        .announcement-banner-lightbox-close::before {
          transform: translate(-50%, -50%) rotate(45deg);
        }
        .announcement-banner-lightbox-close::after {
          transform: translate(-50%, -50%) rotate(-45deg);
        }

        @media (max-width: 860px) {
          .announcement-banner {
            min-height: 0;
            border-radius: 22px;
          }
          .announcement-banner-grid {
            grid-template-columns: 1fr;
            gap: 22px;
            min-height: 0;
            padding: 26px 22px 30px;
            text-align: center;
          }
          .announcement-banner-media {
            justify-self: center;
            max-width: min(260px, 78vw);
            max-height: none;
          }
          .announcement-banner-copy {
            max-width: 100%;
            text-align: center;
          }
          .announcement-banner-title {
            margin-left: auto;
            margin-right: auto;
            line-height: 1.03;
          }
          .announcement-banner-message {
            margin-left: auto;
            margin-right: auto;
          }
          .announcement-banner-actions {
            justify-content: center;
          }
        }

        @media (max-width: 520px) {
          .announcement-banner {
            margin-bottom: 38px;
            border-radius: 18px;
          }
          .announcement-banner-grid {
            padding: 22px 16px 24px;
          }
          .announcement-banner-title {
            font-size: clamp(28px, 10vw, 40px);
          }
          .announcement-banner-actions {
            gap: 10px;
          }
          .announcement-banner-link,
          .announcement-banner-btn {
            width: 100%;
          }
          .announcement-banner-btn {
            min-width: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnnouncementBanner;
